/**
 * Positions, per exchange account.
 *
 * Accounts are listed separately rather than combined. Capital is isolated per
 * exchange, and a single total would imply funds can move between venues.
 *
 * Frozen capital gets its own figure next to available, for the same reason:
 * money the loss-exit policy is holding is not money that can be deployed.
 *
 * The figures add up on screen: cash free, cash in open orders, positions and
 * any other coins make the account's total worth, and on paper that is set
 * against what was put in. When the exchange holds coins the ledger has no
 * record of (or the reverse), it says so, rather than the totals silently
 * disagreeing.
 */
import { useEffect, useState } from 'react';

import { fetchPortfolio, fundPaperAccount } from '../../api/endpoints';
import type { AccountPortfolio, ControlRequestRow, Position } from '../../api/types';
import { Notice } from '../../components/Notice';
import { RequirePermission } from '../../components/RequirePermission';
import { describeSale } from '../../lib/controlRequests';
import { describe as describeError } from '../../lib/errors';
import { formatDateTime, formatMoney, formatPrice, formatQuantity, signOf } from '../../lib/format';
import { EmergencyExitDialog } from './EmergencyExitDialog';

/** Coins listed in a disagreement before the rest are only counted. */
const DRIFT_SHOWN = 10;

export function PositionsPage() {
  const [accounts, setAccounts] = useState<AccountPortfolio[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [funding, setFunding] = useState<Record<number, string>>({});
  const [exiting, setExiting] = useState<{ account: AccountPortfolio; position: Position } | null>(
    null,
  );
  const [exitResult, setExitResult] = useState<ControlRequestRow | null>(null);

  const load = () =>
    fetchPortfolio()
      .then(setAccounts)
      .catch((cause) => setError(describeError(cause)))
      .finally(() => setLoading(false));

  useEffect(() => {
    void load();
  }, []);

  async function fund(accountId: number) {
    const amount = (funding[accountId] ?? '').trim();
    if (!amount) return;
    try {
      await fundPaperAccount(accountId, amount);
      setFunding((current) => ({ ...current, [accountId]: '' }));
      await load();
    } catch (cause) {
      setError(describeError(cause));
    }
  }

  return (
    <section className="page">
      <header className="page__header">
        <h1>Positions</h1>
        <p className="page__lead">
          One panel per exchange account. Capital is not shared between exchanges.
        </p>
      </header>

      {error ? <Notice tone="error">{error}</Notice> : null}
      {exitResult ? (
        <Notice tone={exitResult.state === 'DONE' ? 'info' : 'error'}>
          Emergency exit of {exitResult.symbol}:{' '}
          {exitResult.state === 'DONE'
            ? describeSale(exitResult.result) +
              (exitResult.result?.cancelled_orders
                ? `, ${String(exitResult.result.cancelled_orders)} open order(s) cancelled`
                : '')
            : exitResult.state === 'PENDING'
              ? 'still queued; the scheduler has not picked it up yet'
              : `failed: ${String(exitResult.result?.error ?? 'unknown error')}`}
        </Notice>
      ) : null}
      {loading ? <p className="muted">Loading…</p> : null}
      {!loading && accounts.length === 0 ? (
        <Notice tone="info">No exchange accounts are configured yet.</Notice>
      ) : null}

      {accounts.map((account) => (
        <article key={account.account_id} className="account" data-testid={`account-${account.account_id}`}>
          <header className="account__head">
            <h2>
              {account.exchange} <span className="muted">{account.label}</span>
            </h2>
            <span className={`mode mode--${account.mode.toLowerCase()}`}>{account.mode}</span>
          </header>

          <dl className="account__totals" data-testid={`summary-${account.account_id}`}>
            {account.summary.funded_quote !== null ? (
              <div>
                <dt
                  title={
                    account.summary.funded_basis === 'balance'
                      ? 'The account balance before any trade: cash, plus what open positions ' +
                        'cost, less realised profit. A deposit at the exchange raises it; it is ' +
                        'not counted as profit.'
                      : 'Every USDT added to this paper account.'
                  }
                >
                  Capital put in
                </dt>
                <dd className="num">{formatMoney(account.summary.funded_quote)}</dd>
              </div>
            ) : null}
            <div>
              <dt>Cash available</dt>
              <dd className="num">{formatMoney(account.summary.cash_free)}</dd>
            </div>
            <div>
              <dt>Cash in open orders</dt>
              <dd className="num">{formatMoney(account.summary.cash_in_orders)}</dd>
            </div>
            <div>
              <dt>Positions</dt>
              <dd className="num">{formatMoney(account.summary.positions_value)}</dd>
            </div>
            {signOf(account.summary.other_coins_value) !== 0 ? (
              <div>
                <dt title="Remainders too small to count as positions, and any coin the ledger has no record of">
                  Other coins
                </dt>
                <dd className="num">{formatMoney(account.summary.other_coins_value)}</dd>
              </div>
            ) : null}
            <div>
              <dt>Total worth</dt>
              <dd className="num">{formatMoney(account.summary.equity)}</dd>
            </div>
            {account.summary.pnl !== null ? (
              <div>
                <dt>Profit / loss</dt>
                <dd className={`num ${signOf(account.summary.pnl) < 0 ? 'loss' : 'gain'}`}>
                  {formatMoney(account.summary.pnl)}
                </dd>
              </div>
            ) : null}
            <div>
              <dt>Frozen</dt>
              <dd className={`num ${Number(account.frozen_quote) > 0 ? 'loss' : ''}`}>
                {formatMoney(account.frozen_quote)}
              </dd>
            </div>
            <div>
              <dt>Realised today</dt>
              <dd className={`num ${account.realised_today.startsWith('-') ? 'loss' : 'gain'}`}>
                {formatMoney(account.realised_today)}
              </dd>
            </div>
          </dl>

          <p className="muted" data-testid={`synced-${account.account_id}`}>
            Total worth is cash available + cash in open orders + every coin the system holds, at
            the last hourly price. Balances last checked with the exchange{' '}
            {formatDateTime(account.summary.last_synced_at)}.
          </p>

          {(account.summary.untracked_assets ?? 0) > 0 ? (
            <p className="muted" data-testid={`untracked-${account.account_id}`}>
              Not counted: {account.summary.untracked_assets} coins this account held before the
              system traded in it
              {signOf(account.summary.untracked_value ?? '0') !== 0
                ? `, worth about ${formatMoney(account.summary.untracked_value ?? '0')}`
                : ''}
              . They are yours, not the system&apos;s: left out of the figures above, and never
              sold.
            </p>
          ) : null}

          {account.reconciliation.drift.length > 0 ? (
            <Notice tone="error">
              The exchange and the ledger disagree:{' '}
              {account.reconciliation.drift
                .slice(0, DRIFT_SHOWN)
                .map(
                  (row) =>
                    `${row.asset} exchange ${formatQuantity(row.exchange_qty, 8)}, ` +
                    `ledger ${formatQuantity(row.ledger_qty, 8)}` +
                    (row.value ? ` (${formatMoney(row.value)})` : ''),
                )
                .join('; ')}
              {account.reconciliation.drift.length > DRIFT_SHOWN
                ? `; and ${account.reconciliation.drift.length - DRIFT_SHOWN} more`
                : ''}
              .
            </Notice>
          ) : null}

          {(account.reconciliation.renamed ?? []).length > 0 ? (
            <p className="muted" data-testid={`renamed-${account.account_id}`}>
              {(account.reconciliation.renamed ?? [])
                .map(
                  (row) =>
                    `The exchange reports the ${row.asset} holding under the name ` +
                    `${row.exchange_asset}`,
                )
                .join('; ')}
              . The quantities match exactly, so it is counted as one holding.
            </p>
          ) : null}

          {Number(account.available_quote) === 0 ? (
            <Notice tone="warn">
              {account.mode === 'PAPER'
                ? 'This paper account has no funds, so every order will be refused for ' +
                  'insufficient balance. Give it something to trade with below.'
                : 'This account has no USDT available at the exchange, so every order will be ' +
                  'refused for insufficient balance. Add USDT at the exchange.'}
            </Notice>
          ) : null}

          {/* Only paper money is added here; demo, testnet and live accounts
              hold what the exchange says they do. */}
          {account.mode === 'PAPER' ? (
            <RequirePermission permission="config:exchange_accounts">
              <div className="fund" data-testid={`fund-${account.account_id}`}>
                <label>
                  <span className="muted">Add paper funds (USDT)</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={funding[account.account_id] ?? ''}
                    onChange={(event) =>
                      setFunding((current) => ({
                        ...current,
                        [account.account_id]: event.target.value,
                      }))
                    }
                    placeholder="1000"
                  />
                </label>
                <button
                  type="button"
                  className="button button--small"
                  onClick={() => void fund(account.account_id)}
                >
                  Fund
                </button>
              </div>
            </RequirePermission>
          ) : null}

          {account.stale_marks.length > 0 ? (
            <Notice tone="warn">
              No recent price for {account.stale_marks.join(', ')}. Those positions are
              valued at cost.
            </Notice>
          ) : null}

          {account.positions.length === 0 ? (
            <p className="muted">No open positions.</p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th className="num">Quantity</th>
                  <th className="num">Entry</th>
                  <th className="num">Mark</th>
                  <th className="num">Value</th>
                  <th className="num">Unrealised</th>
                  <th className="num">Take-profit</th>
                  <th className="num">Held</th>
                  <th>State</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {account.positions.map((position) => (
                  <tr key={position.symbol} data-testid={`position-${position.symbol}`}>
                    <td>{position.symbol}</td>
                    <td className="num">{formatQuantity(position.qty, 8)}</td>
                    <td className="num">{formatPrice(position.average_entry)}</td>
                    <td className="num">{formatPrice(position.market_price)}</td>
                    <td className="num">{formatMoney(position.market_value)}</td>
                    <td className={`num ${position.unrealised.startsWith('-') ? 'loss' : 'gain'}`}>
                      {formatMoney(position.unrealised)}
                      {position.unrealised_pct ? (
                        <span className="muted"> ({formatQuantity(position.unrealised_pct, 1)}%)</span>
                      ) : null}
                    </td>
                    <td className="num" data-testid={`take-profit-${position.symbol}`}>
                      {position.take_profit?.price ? (
                        formatPrice(position.take_profit.price)
                      ) : (
                        <span className="muted">not placed yet</span>
                      )}
                    </td>
                    <td className="num">{position.held_days}d</td>
                    <td>
                      {position.frozen ? (
                        <span className="tag tag--frozen" title={position.frozen_reason ?? ''}>
                          frozen
                        </span>
                      ) : (
                        <span className="muted">open</span>
                      )}
                    </td>
                    <td className="num">
                      <RequirePermission permission="danger:liquidation">
                        <button
                          type="button"
                          className="button button--small danger"
                          onClick={() => {
                            setExitResult(null);
                            setExiting({ account, position });
                          }}
                        >
                          Emergency exit
                        </button>
                      </RequirePermission>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </article>
      ))}

      {exiting ? (
        <EmergencyExitDialog
          accountId={exiting.account.account_id}
          accountLabel={`${exiting.account.exchange} ${exiting.account.label}`}
          mode={exiting.account.mode}
          position={exiting.position}
          onClose={(result) => {
            setExiting(null);
            if (result) {
              setExitResult(result);
              void load();
            }
          }}
        />
      ) : null}
    </section>
  );
}
